import React, { useState } from 'react';
import { EnhancedProductLineItem } from '~/utils/use-cart-items';
import useProductMainImage from '~/hooks/components/use-product-main-image';
import LineItemGeneric from './LineItemGeneric';
import { useCart } from '@chordcommerce/react-autonomy';

type Props = {
  item: EnhancedProductLineItem;
  confirmed?: boolean;
};

export type GiftCardPresentation = {
  id: number;
  'Send to': string;
  Email: string;
};

const GiftCardLineItem = ({ item, confirmed }: Props) => {
  const {
    variant: { sku, slug, optionValues },
    quantity,
    productName,
    displayAmount,
    giftCards,
  } = item;
  const { cart, removeGiftCardItem } = useCart();
  const amountOption = optionValues?.find((o) => o?.option_type?.[0]?.slug === 'amount');
  const priceWithoutDecimal = displayAmount.split('.')[0];
  const { image } = useProductMainImage({ sku, slug });
  const [isRemovingGiftCard, setIsRemovingGiftCard] = useState<number | null>(null);
  const [isRemovingGiftCardError, setIsRemovingGiftCardError] = useState<{ id: number; error: string } | null>(null);
  const removeGiftCard = async (giftCardId: number) => {
    setIsRemovingGiftCard(giftCardId);
    try {
      await removeGiftCardItem({
        order: { number: cart.number ?? '', token: cart.token ?? '' },
        redemptionCode: giftCardId.toString(),
      });
    } catch (error) {
      console.error('Error removing gift card', error);
      setIsRemovingGiftCardError({
        id: giftCardId,
        error: error instanceof Error ? `Error removing gift card: ${error.message}` : 'Error removing gift card',
      });
    } finally {
      setIsRemovingGiftCard(null);
    }
  };

  const giftCardsData: GiftCardPresentation[] = giftCards.map((giftCard) => {
    const { id, recipientEmail, recipientName } = giftCard;

    const sendTo = recipientName || (!recipientName && !recipientEmail ? 'Yourself' : '');

    const formattedGiftCard: GiftCardPresentation = {
      id: id,
      'Send to': sendTo,
      Email: recipientEmail || '',
    };

    return formattedGiftCard;
  });

  return (
    <LineItemGeneric
      image={image}
      title={productName}
      quantity={quantity}
      id={item.id}
      priceWithoutDecimal={priceWithoutDecimal}
      confirmed={confirmed}
      isGiftCard={true}
      giftCards={giftCardsData}
      amountPresentation={amountOption?.presentation ?? ''}
      removeGiftCard={removeGiftCard}
      isRemovingGiftCard={isRemovingGiftCard}
      isRemovingGiftCardError={isRemovingGiftCardError}
    />
  );
};

export default GiftCardLineItem;
